import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  sectionName: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

interface ErrorLogEntry {
  timestamp: string;
  sectionName: string;
  errorMessage: string;
  errorStack: string | undefined;
}

const ERROR_LOG_KEY = 'app_errors';
const MAX_ERROR_LOG_ENTRIES = 100; // Prevent localStorage from growing too large

/**
 * ErrorBoundary component that catches rendering errors in child components
 * and displays a fallback UI with error information and recovery options.
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { sectionName } = this.props;

    // Update state with error info
    this.setState({ errorInfo });

    // Log to console
    console.error(`[ErrorBoundary] Error in ${sectionName}:`, error);
    console.error('Error Info:', errorInfo);

    // Log to localStorage
    this.logErrorToStorage(error, errorInfo);
  }

  /**
   * Logs error to localStorage for debugging and analysis
   */
  private logErrorToStorage(error: Error, errorInfo: ErrorInfo): void {
    try {
      const { sectionName } = this.props;

      // Create error log entry
      const logEntry: ErrorLogEntry = {
        timestamp: new Date().toISOString(),
        sectionName,
        errorMessage: error.message,
        errorStack: error.stack
      };

      // Get existing logs
      const existingLogsJson = localStorage.getItem(ERROR_LOG_KEY);
      const existingLogs: ErrorLogEntry[] = existingLogsJson ? JSON.parse(existingLogsJson) : [];

      // Add new log entry
      const updatedLogs = [logEntry, ...existingLogs].slice(0, MAX_ERROR_LOG_ENTRIES);

      // Save to localStorage
      localStorage.setItem(ERROR_LOG_KEY, JSON.stringify(updatedLogs));

      console.log(`[ErrorBoundary] Error logged to localStorage (${updatedLogs.length} total errors)`);
    } catch (storageError) {
      console.error('[ErrorBoundary] Failed to log error to localStorage:', storageError);
    }
  }

  /**
   * Resets the error boundary state, allowing the component tree to re-render
   */
  private handleReload = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, sectionName } = this.props;

    if (hasError && error) {
      return (
        <div style={styles.container}>
          <div style={styles.card}>
            {/* Error Icon */}
            <div style={styles.iconContainer}>
              <svg
                width="64"
                height="64"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ef4444"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>

            {/* Error Title */}
            <h2 style={styles.title}>Something went wrong</h2>

            {/* Section Name */}
            <p style={styles.sectionName}>Error in: {sectionName}</p>

            {/* User-friendly Error Message */}
            <p style={styles.message}>
              We're sorry, but an unexpected error occurred. This error has been logged and you can try reloading this
              section.
            </p>

            {/* Error Details (for developers) */}
            <details style={styles.details}>
              <summary style={styles.detailsSummary}>Technical Details</summary>
              <div style={styles.detailsContent}>
                <p style={styles.errorMessage}>
                  <strong>Error:</strong> {error.message}
                </p>
                {error.stack && (
                  <pre style={styles.stackTrace}>
                    <code>{error.stack}</code>
                  </pre>
                )}
              </div>
            </details>

            {/* Reload Button */}
            <button
              onClick={this.handleReload}
              style={styles.reloadButton}
              onMouseEnter={e => {
                e.currentTarget.style.backgroundColor = '#1976D2';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.backgroundColor = '#2196F3';
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={styles.reloadIcon}
              >
                <polyline points="23 4 23 10 17 10"></polyline>
                <polyline points="1 20 1 14 7 14"></polyline>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
              Reload Section
            </button>
          </div>
        </div>
      );
    }

    return children;
  }
}

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '40px 20px',
    backgroundColor: '#f8fafc'
  },
  card: {
    maxWidth: '600px',
    width: '100%',
    backgroundColor: 'white',
    borderRadius: '12px',
    padding: '40px',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    textAlign: 'center'
  },
  iconContainer: {
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'center'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1f2937',
    margin: '0 0 16px 0'
  },
  sectionName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#6b7280',
    margin: '0 0 24px 0',
    padding: '8px 16px',
    backgroundColor: '#f3f4f6',
    borderRadius: '6px',
    display: 'inline-block'
  },
  message: {
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#4b5563',
    margin: '0 0 32px 0'
  },
  details: {
    textAlign: 'left',
    marginBottom: '32px',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
    overflow: 'hidden'
  },
  detailsSummary: {
    padding: '12px 16px',
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '14px',
    color: '#374151',
    userSelect: 'none'
  },
  detailsContent: {
    padding: '16px',
    borderTop: '1px solid #e5e7eb'
  },
  errorMessage: {
    fontSize: '14px',
    color: '#374151',
    margin: '0 0 16px 0',
    wordBreak: 'break-word'
  },
  stackTrace: {
    fontSize: '12px',
    color: '#6b7280',
    backgroundColor: '#1f2937',
    padding: '16px',
    borderRadius: '6px',
    overflow: 'auto',
    maxHeight: '200px',
    margin: 0
  },
  reloadButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 32px',
    backgroundColor: '#2196F3',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    fontFamily: 'inherit'
  },
  reloadIcon: {
    display: 'inline-block'
  }
};
